import { useCallback, useEffect, useState } from 'react';
import type { Message, MessageWithSender, Profile } from '../types';
import { getSupabase, isSupabaseConfigured } from '../lib/supabase';

export function useMessages(conversationId: string | null) {
  const [messages, setMessages] = useState<MessageWithSender[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadMessages = useCallback(async () => {
    if (!conversationId || !isSupabaseConfigured) {
      setMessages([]);
      return;
    }

    const client = getSupabase();
    setLoading(true);
    setError(null);

    const { data, error: messagesError } = await client
      .from('messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true })
      .limit(200);

    if (messagesError) {
      setError(messagesError.message);
      setLoading(false);
      return;
    }

    const rawMessages = (data ?? []) as Message[];
    const senderIds = [...new Set(rawMessages.map((message) => message.sender_id))];
    const profileMap = new Map<string, Profile>();

    if (senderIds.length > 0) {
      const { data: profiles } = await client.from('profiles').select('*').in('id', senderIds);
      ((profiles ?? []) as Profile[]).forEach((profile) => profileMap.set(profile.id, profile));
    }

    setMessages(
      rawMessages.map((message) => ({
        ...message,
        sender: profileMap.get(message.sender_id) ?? null,
      })),
    );
    setLoading(false);
  }, [conversationId]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    if (!conversationId || !isSupabaseConfigured) return;

    const client = getSupabase();
    const channel = client
      .channel(`messages:${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        () => {
          loadMessages();
        },
      )
      .subscribe();

    return () => {
      client.removeChannel(channel);
    };
  }, [conversationId, loadMessages]);

  return { messages, loading, error, reload: loadMessages };
}

